import WatchlistClient from './WatchlistClient';
import HttpClient from '../../gateway/HttpClient';
import { notBlank, notUndefined } from '../../util/Assert';
import HttpMethod from '../../constant/HttpMethod';
import { WatchlistMethods } from '../../constant/MethodNames';
import {
  WatchlistBaseModel,
  WatchlistConfiguration,
  WatchlistEntityRequest,
  WatchlistGeneralRequest,
  WatchlistIndividualRequest,
  WatchlistListsModel,
  SafeListConfig,
  SafeListRequest,
  WatchlistByIdResponse,
} from '../../domain/watchlist/WatchlistDomains';
import { WatchlistType } from '../../constant/Constants';
import { query } from '@trlogic/client-js';
import { AlertCommentLog, AlertStatus, WatchlistAlertDomains } from '../../domain/watchlist/WatchlistAlertDomains';
import { WatchlistNewsDomains } from '../../domain/watchlist/WatchlistNewsDomains';
import { CustomerRequest, WatchlistCustomerDomains } from '../../domain/watchlist/WatchlistCustomerDomains';
import { CancelToken } from 'axios';
import { MatchManyLogConfig, MatchManyLogCount } from '../../domain/watchlist/WatchlistHistoryDomains';

class NestWatchlistClient implements WatchlistClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    notUndefined(httpClient, 'http client can not be undefined');
    this.httpClient = httpClient;
  }

  public searchMany(
    request: Partial<WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest>,
    type: WatchlistType,
  ): Promise<WatchlistBaseModel[]> {
    const searchDto = { request, type };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.SEARCH_MANY, searchDto);
  }

  public searchOne(
    request: Partial<WatchlistIndividualRequest | WatchlistEntityRequest | WatchlistGeneralRequest>,
    type: WatchlistType,
  ): Promise<WatchlistBaseModel> {
    const searchDto = { request, type };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.SEARCH_ONE, searchDto);
  }

  public getById(type: WatchlistType, id: string): Promise<WatchlistByIdResponse> {
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    return this.httpClient.execute(HttpMethod.GET, `/watchlist/${type}/${id}`);
  }

  public getIndividualId(listId: string, relationId: string): Promise<WatchlistByIdResponse> {
    notUndefined(listId, 'List id cannot be undefined');
    notBlank(listId, 'List id cannot be blank');
    notUndefined(relationId, 'Relation id cannot be undefined');
    notBlank(relationId, 'Relation id cannot be blank');

    return this.httpClient.execute(HttpMethod.GET, `/watchlist/relation-detail/${listId}/${relationId}`);
  }

  public getLists(): Promise<WatchlistListsModel[]> {
    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.GET_LISTS, {});
  }

  public getKycLogCount(index: string, _query: query.SelectQuery): Promise<number> {
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(_query, 'Query cannot be undefined');
    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.GET_KYC_LOG_COUNT, undefined, { index, query: _query });
  }

  public closeKycLog(index: string, id: string, type: AlertStatus, reason?: string): Promise<void> {
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const dto = {
      index: index,
      id: id,
      type: type,
      reason: reason,
    };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.CLOSE_KYC_LOG, dto);
  }

  public imageConvertBase64(url: string): Promise<string> {
    notUndefined(url, 'Url cannot be undefined');
    notBlank(url, 'Url cannot be blank');

    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.CONVERT_BASE64, undefined, { url });
  }

  public closeKycLogBulk(index: string, ids: string[], type: AlertStatus, selectQuery?: query.SelectQuery): Promise<void> {
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');

    const dto = {
      index: index,
      ids: ids,
      type: type,
      query: selectQuery,
    };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.CLOSE_KYC_LOG_BULK, dto);
  }

  public goKycLog(index: string, id: string): Promise<void> {
    notUndefined(index, 'Index cannot be undefined');
    notBlank(index, 'Index cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    const dto = {
      index: index,
      id: id,
    };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.GO_KYC_LOG, dto);
  }

  public getNews(name: string, region: string, exactMatch?: boolean): Promise<WatchlistNewsDomains> {
    notUndefined(name, 'Name cannot be undefined');
    notBlank(name, 'Name cannot be blank');
    notUndefined(region, 'Region cannot be undefined');
    notBlank(region, 'Region cannot be blank');

    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.GET_NEWS, undefined, this.buildNewsQuery(name, region, exactMatch));
  }

  private buildNewsQuery(name: string, region: string, exactMatch?: boolean): { name: string; region: string; exactMatch?: string } {
    const query: { name: string; region: string; exactMatch?: string } = { name, region };
    if (exactMatch !== undefined) {
      query.exactMatch = String(exactMatch);
    }
    return query;
  }

  public customerLookup(body: CustomerRequest): Promise<WatchlistCustomerDomains> {
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.CUSTOMER_LOOKUP, body);
  }

  public saveConfiguration(body: WatchlistConfiguration): Promise<void> {
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.SAVE_CONFIGURATION, body);
  }

  public deleteConfiguration(id: string): Promise<void> {
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    return this.httpClient.execute(HttpMethod.DELETE, `${WatchlistMethods.DELETE_CONFIGURATION}/${id}`);
  }

  public sendComment(id: string, comment: string): Promise<AlertCommentLog> {
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');
    notUndefined(comment, 'Comment cannot be undefined');
    notBlank(comment, 'Comment cannot be blank');

    const commentPayload = { id, comment };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.COMMENT, commentPayload);
  }

  public getAlertCounts(): Promise<Record<string, number>> {
    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.GET_ALERT_COUNTS);
  }

  public getAlertPageData(
    type: WatchlistType,
    _query: query.SelectQuery,
    token: CancelToken,
  ): Promise<{
    data: WatchlistAlertDomains[];
    count: number;
  }> {
    const body = {
      type,
      query: _query,
    };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.GET_ALERT_PAGE_DATA, body, undefined, undefined, undefined, token);
  }

  public assignAlert(type: WatchlistType, id: string, assignee: string): Promise<void> {
    notUndefined(type, 'Type cannot be undefined');
    notBlank(type, 'Type cannot be blank');
    notUndefined(id, 'Id cannot be undefined');
    notBlank(id, 'Id cannot be blank');

    const dto = {
      type: type,
      id: id,
      assignee: assignee,
    };
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.ASSIGN_ALERT, dto);
  }

  public addSafeList(request: SafeListRequest): Promise<void> {
    notUndefined(request, 'Safe list request cannot be undefined');
    notBlank(request.referenceId, 'Reference id cannot be blank');
    notUndefined(request.consolidatedRecordIds, 'Consolidated record ids cannot be undefined');
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.SAFE_LIST_ADD, request);
  }

  public removeSafeList(request: SafeListRequest): Promise<void> {
    notUndefined(request, 'Safe list request cannot be undefined');
    notBlank(request.referenceId, 'Reference id cannot be blank');
    notUndefined(request.consolidatedRecordIds, 'Consolidated record ids cannot be undefined');
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.SAFE_LIST_REMOVE, request);
  }

  public getSafeListConfig(): Promise<SafeListConfig> {
    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.SAFE_LIST_CONFIG);
  }

  public saveSafeListConfig(enabled: boolean): Promise<SafeListConfig> {
    return this.httpClient.execute(HttpMethod.PUT, WatchlistMethods.SAFE_LIST_CONFIG, { enabled });
  }

  public getMatchManyLogCount(filter: query.Filter): Promise<MatchManyLogCount> {
    return this.httpClient.execute(HttpMethod.POST, WatchlistMethods.MATCH_MANY_LOG_COUNT, { filter });
  }

  public getMatchManyLogConfig(): Promise<MatchManyLogConfig> {
    return this.httpClient.execute(HttpMethod.GET, WatchlistMethods.MATCH_MANY_LOG_CONFIG);
  }

  public saveMatchManyLogConfig(minScore: number, maxRecords: number): Promise<MatchManyLogConfig> {
    return this.httpClient.execute(HttpMethod.PUT, WatchlistMethods.MATCH_MANY_LOG_CONFIG, { minScore, maxRecords });
  }
}

export default NestWatchlistClient;
